import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { addColorPalettes, prependColorPalette, removeColorPalette, updateColorPalette, resetBrandKit, setBrandKit } from '../../features/brand-kit/brandKitSlice';
import { useCreateBrandKitMutation, useGetBrandKitByIdQuery, useUpdateBrandKitMutation } from '../../features/brand-kit/brandKitApi';
import svgPaths from "./imports/svg-xtcf4h81tk";
import svgPathsSelected from "./imports/svg-1rzn3fo5gk";
import clsx from "clsx";
import { motion } from "motion/react";
import ColorSelectionModal, { ColorPalette as ModalColorPalette } from './components/ColorSelectionModal';
import { CreateNewBrandKitModal } from './components/CreateNewBrandKitModal';
import { HexColorPicker } from 'react-colorful';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { ColorPalette } from '../../features/brand-kit/brandKitSlice';

// Helper to generate IDs
const generateId = () => Math.random().toString(36).substr(2, 9);

function ColorPickerContent({ initialColor = "#000000", onSave, buttonText = "Add Color" }: { initialColor?: string, onSave: (color: string) => void, buttonText?: string }) {
  const [color, setColor] = useState(initialColor);

  return (
    <div className="flex flex-col gap-3">
      <HexColorPicker color={color} onChange={setColor} style={{ width: '100%' }} />
      <div className="flex gap-2">
        <div className="w-9 h-9 rounded border shrink-0" style={{ backgroundColor: color }} />
        <input
          className="flex h-9 w-full rounded-md border border-[#e4e7ec] bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#227735] disabled:cursor-not-allowed disabled:opacity-50"
          value={color}
          onChange={(e) => setColor(e.target.value)}
        />
      </div>
      <button
        className="w-full bg-[#30A84B] text-white hover:bg-[#288e3e] shadow h-9 px-4 py-2 inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 transition-colors"
        onClick={() => onSave(color)}
      >
        {buttonText}
      </button>
    </div>
  )
}

function Frame({ children, onClick }: React.PropsWithChildren<{ onClick?: () => void }>) {
  return (
    <div className={clsx("relative shrink-0 size-[20px]", onClick && "cursor-pointer")} onClick={onClick}>
      <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 20 20">
        {children}
      </svg>
    </div>
  );
}



type PrimarySmTextProps = {
  text: string;
  additionalClassNames?: string;
  onClick?: () => void;
};

function PrimarySmText({ text, additionalClassNames = "", onClick }: PrimarySmTextProps) {
  return (
    <div
      className={clsx("content-stretch flex gap-[4px] h-[36px] items-center justify-center p-[12px] relative rounded-[6px] shrink-0", additionalClassNames, onClick && "cursor-pointer")}
      onClick={onClick}
    >
      <p className="font-sans font-semibold leading-[normal] relative shrink-0 text-[14px] text-nowrap text-white" style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
        {text}
      </p>
    </div>
  );
}

// --- Icons ---

function SearchIcon() {
  return (
    <div className="relative shrink-0 size-[16px]">
      <div className="absolute contents inset-0">
        <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 16 16">
          <g id="search-normal">
            <path d={svgPathsSelected.p20103e00} id="Vector" stroke="var(--stroke-0, #98A2B3)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.25" />
            <path d={svgPathsSelected.p133f8470} id="Vector_2" stroke="var(--stroke-0, #98A2B3)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.25" />
          </g>
        </svg>
      </div>
    </div>
  );
}

function AddIcon() {
  return (
    <div className="relative shrink-0 size-[18px]">
      <div className="absolute contents inset-0">
        <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 18 18">
          <g id="add">
            <path d="M4.5 9H13.5" id="Vector" stroke="var(--stroke-0, #292D32)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.25" />
            <path d="M9 13.5V4.5" id="Vector_2" stroke="var(--stroke-0, #292D32)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.25" />
          </g>
        </svg>
      </div>
    </div>
  );
}

function DeleteIcon() {
  return (
    <div className="relative shrink-0 size-[14px]">
      <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 14 14">
        <g id="Frame">
          <path d={svgPathsSelected.p2d36180} id="Vector" stroke="var(--stroke-0, #F04438)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.25" />
          <path d="M1.75 3.5H12.25" id="Vector_2" stroke="var(--stroke-0, #F04438)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.25" />
          <path d={svgPathsSelected.p3fb90000} id="Vector_3" stroke="var(--stroke-0, #F04438)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.25" />
        </g>
      </svg>
    </div>
  )
}

function EditIcon() {
  return (
    <div className="relative shrink-0 size-[14px]">
      <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 20 20">
        <path d="M17.645 5.67667C18.0856 5.23619 18.3332 4.63872 18.3332 4.01571C18.3333 3.3927 18.0859 2.79517 17.6454 2.35458C17.2049 1.91399 16.6075 1.66643 15.9845 1.66635C15.3614 1.66627 14.7639 1.91369 14.3233 2.35417L3.20167 13.4783C3.00818 13.6712 2.86509 13.9088 2.785 14.17L1.68417 17.7967C1.66263 17.8687 1.661 17.9453 1.67946 18.0182C1.69792 18.0911 1.73577 18.1577 1.789 18.2108C1.84223 18.264 1.90885 18.3017 1.9818 18.3201C2.05475 18.3384 2.1313 18.3367 2.20333 18.315L5.83083 17.215C6.09181 17.1356 6.32931 16.9934 6.5225 16.8008L17.645 5.67667Z" stroke="#344054" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
        <path d="M12.5 4.16667L15.8333 7.5" stroke="#344054" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
      </svg>
    </div>
  )
}

function AddColorIcon() {
  return (
    <div className="relative shrink-0 size-[24px]">
      <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 24 24">
        <path d="M6 12H18" stroke="#227735" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
        <path d="M12 18V6" stroke="#227735" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
      </svg>
    </div>
  )
}

// --- Components ---

function PaletteCard({
  palette,
  onDelete,
  onUpdate
}: {
  palette: ColorPalette;
  onDelete: () => void;
  onUpdate: (changes: Partial<ColorPalette>) => void;
}) {
  const [isEditingName, setIsEditingName] = useState(false);
  const [tempName, setTempName] = useState(palette.name);

  const isCustom = palette.isCustom;

  const handleNameSave = () => {
    setIsEditingName(false);
    if (tempName.trim() && tempName !== palette.name) {
      onUpdate({ name: tempName });
    } else {
      setTempName(palette.name);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleNameSave();
    if (e.key === 'Escape') {
      setIsEditingName(false);
      setTempName(palette.name);
    }
  };

  const handleColorAdd = (color: string) => {
    const newColors = [...palette.colors, color];
    onUpdate({ colors: newColors });
  };

  const handleColorUpdate = (index: number, color: string) => {
    const newColors = [...palette.colors];
    newColors[index] = color;
    onUpdate({ colors: newColors });
  };

  return (
    <div className="relative rounded-[4px] shrink-0 w-full group shadow-sm bg-white">
      <div className="content-stretch flex flex-col items-start overflow-clip px-[12px] py-[14px] relative rounded-[inherit]">
        <div className="content-stretch flex flex-col gap-[16px] items-start relative shrink-0 w-full">
          {/* Header */}
          <div className="content-stretch flex gap-[10px] h-[24px] items-center justify-between relative shrink-0 w-full">
            <div className="flex gap-[8px] items-center grow">
              {isEditingName ? (
                <input
                  autoFocus
                  className="basis-0 grow min-h-px min-w-px text-[13px] font-medium border-b border-gray-300 outline-none"
                  value={tempName}
                  onChange={(e) => setTempName(e.target.value)}
                  onBlur={handleNameSave}
                  onKeyDown={handleKeyDown}
                />
              ) : (
                <div className="flex gap-[4px] items-center">
                  <p className="font-sans font-medium text-[#101928] text-[13px]">
                    {palette.name}
                  </p>
                  {isCustom && (
                    <div className="cursor-pointer p-1 rounded hover:bg-gray-100" onClick={() => setIsEditingName(true)}>
                      <EditIcon />
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-gray-100" onClick={onDelete}>
              <DeleteIcon />
            </div>
          </div>

          {/* Colors */}
          <div className="content-stretch flex gap-[12px] h-[28px] items-center relative shrink-0 w-full">
            {palette.colors.map((color, idx) => {
              if (isCustom) {
                return (
                  <Popover key={idx}>
                    <PopoverTrigger asChild>
                      <div
                        className="h-[28px] rounded-[4px] shrink-0 flex-1 min-w-[30px] cursor-pointer hover:opacity-90 transition-opacity shadow-sm"
                        style={{ backgroundColor: color }}
                      />
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-3 bg-white border border-[#e4e7ec] shadow-lg rounded-lg">
                      <ColorPickerContent
                        initialColor={color}
                        onSave={(newColor) => handleColorUpdate(idx, newColor)}
                        buttonText="Save Color"
                      />
                    </PopoverContent>
                  </Popover>
                );
              }
              return (
                <div key={idx} className="h-[28px] rounded-[4px] shrink-0 flex-1 min-w-[30px]" style={{ backgroundColor: color }} />
              );
            })}

            {isCustom && palette.colors.length < 6 && (
              <Popover>
                <PopoverTrigger asChild>
                  <div className="h-[28px] w-[48px] rounded-[4px] shrink-0 bg-gradient-to-b from-[rgba(11,162,155,0.05)] to-[rgba(67,180,141,0.05)] flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity">
                    <AddColorIcon />
                  </div>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-3 bg-white border border-[#e4e7ec] shadow-lg rounded-lg">
                  <ColorPickerContent onSave={handleColorAdd} />
                </PopoverContent>
              </Popover>
            )}
          </div>
        </div>
      </div>
      <div aria-hidden="true" className="absolute border-[#e4e7ec] border-[0.75px] border-solid inset-0 pointer-events-none rounded-[4px]" />
    </div>
  );
}

// --- Empty State ---

function ColorsEmptyState({ onAddColors }: { onAddColors: () => void }) {
  return (
    <motion.div
      className="absolute content-stretch flex flex-col gap-[16px] items-center left-[calc(50%+0.5px)] top-1/2 translate-x-[-50%] translate-y-[-50%]"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="content-stretch flex flex-col gap-[12px] items-center relative shrink-0">
        <div className="relative shrink-0 size-[111px]" data-name="Title=No favorites; Cololr=purple; Fill=gradient;">
          <div className="absolute h-[92.692px] left-[6.73px] top-[15.1px] w-[89.757px]" data-name="background">
            <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 90 93">
              <g id="background">
                <circle cx="51.523" cy="51.0875" fill="var(--fill-0, #E4E7EC)" id="Ellipse 5" r="38.2339" />
                <circle cx="8.71428" cy="40.3036" fill="var(--fill-0, #E4E7EC)" id="Ellipse 4" r="8.71428" />
                <circle cx="25.4894" cy="5.44644" fill="var(--fill-0, #E4E7EC)" id="Ellipse 3" r="5.44643" />
                <circle cx="85.509" cy="12.5268" fill="var(--fill-0, #E4E7EC)" id="Ellipse 2" r="4.24821" />
                <circle cx="84.2016" cy="76.3589" fill="var(--fill-0, #E4E7EC)" id="Ellipse 1" r="2.06964" />
                <path d={svgPaths.p1fc73800} fill="var(--fill-0, #D0D5DD)" id="Star 1" />
                <path d={svgPaths.p10e5c0f0} fill="var(--fill-0, #D0D5DD)" id="Star 2" />
                <path d={svgPaths.pf4dfa40} fill="var(--fill-0, #D0D5DD)" id="Star 3" />
              </g>
            </svg>
          </div>
          <div className="absolute contents left-[11.73px] top-[25.92px]" data-name="illustration">
            <div className="absolute contents left-[60.54px] top-[37.32px]" data-name="tag 2">
              <div className="absolute flex h-[57.42px] items-center justify-center left-[60.54px] top-[37.32px] w-[40.809px]" style={{ "--transform-inner-width": "0", "--transform-inner-height": "0" } as React.CSSProperties}>
                <div className="flex-none rotate-[10.848deg]">
                  <div className="h-[52.427px] relative w-[31.505px]">
                    <div className="absolute inset-[0_0_1.3%_0]">
                      <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 32 52">
                        <path d={svgPaths.p2b005980} fill="var(--fill-0, #D0D5DD)" id="Vector 2" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
              <div className="absolute flex h-[15.794px] items-center justify-center left-[74.04px] top-[49.52px] w-[17.011px]" style={{ "--transform-inner-width": "0", "--transform-inner-height": "0" } as React.CSSProperties}>
                <div className="flex-none rotate-[12.059deg]">
                  <div className="h-[13.029px] relative w-[14.612px]">
                    <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 15 14">
                      <path d={svgPaths.p2a61380} fill="var(--fill-0, white)" id="Vector 1" />
                    </svg>
                  </div>
                </div>
              </div>
              <div className="absolute left-[75.35px] size-[5.664px] top-[78.93px]" data-name="Ellipse">
                <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 6 6">
                  <circle cx="2.83214" cy="2.83214" fill="var(--fill-0, #E4E7EC)" id="Ellipse" r="2.83214" />
                </svg>
              </div>
            </div>
            <div className="absolute contents left-[11.73px] top-[25.92px]" data-name="tag 1">
              <div className="absolute flex h-[80.313px] items-center justify-center left-[11.73px] top-[25.92px] w-[63.525px]" style={{ "--transform-inner-width": "0", "--transform-inner-height": "0" } as React.CSSProperties}>
                <div className="flex-none rotate-[340.089deg]">
                  <div className="h-[70.15px] relative w-[42.155px]">
                    <div className="absolute inset-[-1.24%_-2.07%_-0.27%_-2.07%]">
                      <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 44 72">
                        <path d={svgPaths.p32f24200} fill="var(--fill-0, #F0F2F5)" id="Vector 2" stroke="var(--stroke-0, #D0D5DD)" strokeWidth="0.871428" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
              <div className="absolute flex h-[26.616px] items-center justify-center left-[25.68px] top-[41.46px] w-[27.98px]" style={{ "--transform-inner-width": "0", "--transform-inner-height": "0" } as React.CSSProperties}>
                <div className="flex-none rotate-[338.575deg]">
                  <div className="h-[19.854px] relative w-[22.266px]">
                    <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 23 20">
                      <path d={svgPaths.p220b2480} fill="var(--fill-0, #D0D5DD)" id="Vector 1" stroke="var(--stroke-0, #D0D5DD)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="0.881822" />
                    </svg>
                  </div>
                </div>
              </div>
              <div className="absolute left-[48.12px] size-[7.189px] top-[82.41px]" data-name="Ellipse">
                <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 8 8">
                  <circle cx="3.59464" cy="3.59464" fill="var(--fill-0, #D0D5DD)" id="Ellipse" r="3.59464" />
                </svg>
              </div>
            </div>
          </div>
        </div>
        <div className="content-stretch flex flex-col gap-[8px] items-center justify-center leading-[0] relative shrink-0 text-nowrap">
          <div className="flex flex-col font-sans font-bold justify-center relative shrink-0 text-[#101928] text-[18px]" style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
            <p className="leading-[normal] text-nowrap">Bring your brand to life with colors!</p>
          </div>
          <div className="flex flex-col font-sans font-medium justify-center relative shrink-0 text-[#475367] text-[14px]" style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
            <p className="leading-[normal] text-nowrap">Pick a few shades that define your vibe — we’ll save them for every design.</p>
          </div>
        </div>
      </div>
      <PrimarySmText text="Choose Colors" additionalClassNames="bg-[#30a84b]" onClick={onAddColors} />
    </motion.div>
  );
}

// --- Selected Colors View ---

function SelectedColorsView({
  palettes,
  onAddClick,
  onDeletePalette,
  onCustomPaletteClick,
  onUpdatePalette
}: {
  palettes: ColorPalette[];
  onAddClick: () => void;
  onDeletePalette: (id: string) => void;
  onCustomPaletteClick: () => void;
  onUpdatePalette: (id: string, changes: Partial<ColorPalette>) => void;
}) {
  const [searchTerm, setSearchTerm] = React.useState("");

  const filteredPalettes = palettes.filter(p => p.name.toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <motion.div
      className="absolute content-stretch flex flex-col gap-[18px] items-start left-[22px] right-[22px] top-[174px]"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="content-stretch flex items-center justify-between relative shrink-0 w-full">
        <div className="bg-white h-[40px] relative rounded-[8px] shrink-0 w-[319px]" data-name="Search bar">
          <div className="content-stretch flex items-center overflow-clip px-[12px] py-[3px] relative rounded-[inherit] size-full">
            <div className="content-stretch flex items-center relative shrink-0 w-full">
              <div className="content-stretch flex gap-[8px] items-center relative flex-1">
                <SearchIcon />
                <input
                  type="text"
                  placeholder="Search palette name..."
                  className="font-sans font-normal leading-[24px] relative flex-1 text-[#101928] text-[13px] bg-transparent outline-none px-2 placeholder-[#98a2b3]"
                  style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </div>
          <div aria-hidden="true" className="absolute border-[#e4e7ec] border-[0.75px] border-solid inset-0 pointer-events-none rounded-[8px]" />
        </div>

        <div className="flex gap-[12px] items-center">
          <div
            className="content-stretch flex gap-[4px] h-[36px] items-center justify-center p-[12px] relative rounded-[6px] shrink-0 cursor-pointer hover:bg-gray-50 transition-colors"
            data-name="Custom Palette"
            onClick={onCustomPaletteClick}
          >
            <div aria-hidden="true" className="absolute border-[#e4e7ec] border-[0.75px] border-solid inset-0 pointer-events-none rounded-[6px]" />
            <p className="font-sans font-medium leading-[normal] relative shrink-0 text-[#101928] text-[13px] text-nowrap" style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
              Custom Palette
            </p>
          </div>
          <div
            className="content-stretch flex gap-[4px] h-[36px] items-center justify-center p-[12px] relative rounded-[6px] shrink-0 cursor-pointer hover:bg-gray-50 transition-colors"
            data-name="Secondary - Sm"
            onClick={onAddClick}
          >
            <div aria-hidden="true" className="absolute border-[#e4e7ec] border-[0.75px] border-solid inset-0 pointer-events-none rounded-[6px]" />
            <AddIcon />
            <p className="font-sans font-medium leading-[normal] relative shrink-0 text-[#101928] text-[13px] text-nowrap" style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
              Add Colors
            </p>
          </div>
        </div>
      </div>

      {/* Palette List */}
      <div className="w-full grid grid-cols-3 gap-[16px]">
        {filteredPalettes.map(palette => (
          <PaletteCard
            key={palette.id}
            palette={palette}
            onDelete={() => onDeletePalette(palette.id)}
            onUpdate={(changes) => onUpdatePalette(palette.id, changes)}
          />
        ))}
      </div>
    </motion.div>
  );
}

// --- Main Component ---

export default function CreateBrandKitColors() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const brandKitName = useAppSelector(state => state.brandKit.name);
  const selectedPalettes = useAppSelector(state => state.brandKit.colors);
  const logos = useAppSelector(state => state.brandKit.logos);
  const [isColorModalOpen, setIsColorModalOpen] = React.useState(false);
  const [isBrandKitModalOpen, setIsBrandKitModalOpen] = React.useState(false);

  const brandKitState = useAppSelector(state => state.brandKit);
  const [createBrandKit, { isLoading: isCreating }] = useCreateBrandKitMutation();
  const [updateBrandKitMutation, { isLoading: isUpdating }] = useUpdateBrandKitMutation();

  const baseUrl = id ? `/brand-kit/edit/${id}` : '/brand-kit/create';
  const { data: brandKitData } = useGetBrandKitByIdQuery(id!, { skip: !id });

  useEffect(() => {
    if (brandKitData && id) {
      dispatch(setBrandKit(brandKitData));
    }
  }, [brandKitData, id, dispatch]);

  const handleCreate = async () => {
    if (isCreating) return;
    try {
      await createBrandKit(brandKitState).unwrap();
      dispatch(resetBrandKit());
      navigate('/brand-kit');
    } catch (error) {
      console.error('Failed to create brand kit:', error);
    }
  };

  const handleUpdate = async () => {
    if (isUpdating || !id) return;
    try {
      await updateBrandKitMutation({ id, data: brandKitState }).unwrap();
      dispatch(resetBrandKit());
      navigate('/brand-kit');
    } catch (error) {
      console.error('Failed to update brand kit:', error);
    }
  };

  const isLoading = isCreating || isUpdating;

  const handleAddPalettes = async (palettes: ModalColorPalette[]) => {
    const newPalettes: ColorPalette[] = palettes
      .filter(p => !selectedPalettes.find((existing: ColorPalette) => existing.name === p.name))
      .map(p => ({
        ...p,
        id: generateId(),
        isCustom: false
      }));

    if (newPalettes.length > 0) {
      dispatch(addColorPalettes(newPalettes));

      if (id) {
        try {
          await updateBrandKitMutation({
            id,
            data: { ...brandKitState, colors: [...brandKitState.colors, ...newPalettes] }
          }).unwrap();
        } catch (error) {
          console.error('Failed to add color palettes:', error);
        }
      }
    }
    setIsColorModalOpen(false);
  };

  const handleDeletePalette = async (paletteId: string) => {
    dispatch(removeColorPalette(paletteId));

    if (id) {
      try {
        await updateBrandKitMutation({
          id,
          data: { ...brandKitState, colors: brandKitState.colors.filter((c: ColorPalette) => c.id !== paletteId) }
        }).unwrap();
      } catch (error) {
        console.error('Failed to delete color palette:', error);
      }
    }
  };

  const handleUpdatePalette = async (paletteId: string, changes: Partial<ColorPalette>) => {
    dispatch(updateColorPalette({ id: paletteId, changes }));

    if (id) {
      try {
        const nextColors = brandKitState.colors.map((c: ColorPalette) =>
          c.id === paletteId ? { ...c, ...changes } : c
        );
        await updateBrandKitMutation({
          id,
          data: { ...brandKitState, colors: nextColors }
        }).unwrap();
      } catch (error) {
        console.error('Failed to update color palette:', error);
      }
    }
  };

  const handleCustomPaletteClick = async () => {
    const newPalette: ColorPalette = {
      id: generateId(),
      name: "New Palette",
      colors: [],
      isCustom: true
    };
    dispatch(prependColorPalette(newPalette));

    if (id) {
      try {
        await updateBrandKitMutation({
          id,
          data: { ...brandKitState, colors: [newPalette, ...brandKitState.colors] }
        }).unwrap();
      } catch (error) {
        console.error('Failed to add custom palette:', error);
      }
    }
  };

  const hasColors = selectedPalettes.length > 0;

  return (
    <div className="bg-white relative size-full min-h-screen" data-name="Create Brand Kit - Colors">
      <ColorSelectionModal
        isOpen={isColorModalOpen}
        onClose={() => setIsColorModalOpen(false)}
        onAddColors={handleAddPalettes}
      />
      <CreateNewBrandKitModal
        isOpen={isBrandKitModalOpen}
        onClose={() => setIsBrandKitModalOpen(false)}
        mode="edit"
        id={id}
      />

      <div className="absolute bg-white h-[72px] left-[22px] right-[22px] rounded-[8px] top-[16px]">
        <div className="content-stretch flex items-center justify-between overflow-clip px-[16px] py-[12px] relative rounded-[inherit] size-full">
          <div className="content-stretch flex gap-[12px] items-center relative shrink-0">
            <Frame onClick={() => navigate('/brand-kit')}>
              <g id="Frame">
                <path d={svgPaths.p33f6b680} id="Vector" stroke="var(--stroke-0, #101928)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
                <path d="M15.8333 10H4.16667" id="Vector_2" stroke="var(--stroke-0, #101928)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
              </g>
            </Frame>
            <div className="content-stretch flex gap-[8px] items-center relative shrink-0">
              <div className="flex flex-col font-sans font-semibold justify-center leading-[0] relative shrink-0 text-[#101928] text-[16px] text-nowrap" style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
                <p className="leading-[normal]">{brandKitName}</p>
              </div>
              <Frame onClick={() => setIsBrandKitModalOpen(true)}>
                <g clipPath="url(#clip0_94_268)" id="Frame">
                  <path d={svgPaths.p30d9cf00} id="Vector" stroke="var(--stroke-0, #344054)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
                  <path d="M12.5 4.16667L15.8333 7.5" id="Vector_2" stroke="var(--stroke-0, #344054)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
                </g>
                <defs>
                  <clipPath id="clip0_94_268">
                    <rect fill="white" height="20" width="20" />
                  </clipPath>
                </defs>
              </Frame>
            </div>
          </div>
          <PrimarySmText
            text={isLoading ? (id ? "Updating..." : "Creating...") : (id ? "Update Brand Kit" : "Create Brand Kit")}
            additionalClassNames={(logos.main || logos.light || logos.dark || selectedPalettes.length > 0) ? "bg-[#30a84b] hover:bg-[#288e3e]" : "bg-[#bfe4c7] cursor-not-allowed"}
            onClick={(logos.main || logos.light || logos.dark || selectedPalettes.length > 0) ? (isLoading ? undefined : (id ? handleUpdate : handleCreate)) : undefined}
          />
        </div>
        <div aria-hidden="true" className="absolute border-[#e4e7ec] border-[0.75px] border-solid inset-0 pointer-events-none rounded-[8px]" />
      </div>
      <div className="absolute flex gap-[12px] items-center left-[22px] top-[120px] bg-gray-50/50 p-1.5 rounded-xl border border-gray-100">
        <div
          className="relative shrink-0 min-w-[100px] h-[36px] flex items-center justify-center overflow-hidden rounded-lg group cursor-pointer"
          onClick={() => navigate(`${baseUrl}/logo`)}
        >
          <div 
              className="app-selector-indicator" 
              style={{ '--background-width': '0%' } as React.CSSProperties} 
          />
          <div className="flex items-center gap-2 relative z-10">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#667185] group-hover:text-[#30A84B] transition-colors">
              <rect width="18" height="18" x="3" y="3" rx="2" ry="2" />
              <circle cx="9" cy="9" r="2" />
              <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
            </svg>
            <span className="font-sans font-medium text-[14px] text-[#667185] group-hover:text-[#30A84B] transition-colors">Logo</span>
          </div>
        </div>

        <div className="relative shrink-0 min-w-[100px] h-[36px] flex items-center justify-center overflow-hidden rounded-lg group">
          <div 
              className="app-selector-indicator" 
              style={{ '--background-width': '100%' } as React.CSSProperties} 
          />
          <div className="flex items-center gap-2 relative z-10">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#30A84B]">
              <circle cx="13.5" cy="6.5" r=".5" fill="currentColor" />
              <circle cx="17.5" cy="10.5" r=".5" fill="currentColor" />
              <circle cx="8.5" cy="7.5" r=".5" fill="currentColor" />
              <circle cx="6.5" cy="12.5" r=".5" fill="currentColor" />
              <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z" />
            </svg>
            <span className="font-sans font-medium text-[14px] app-active-state">Colours</span>
          </div>
        </div>

        <div
          className="relative shrink-0 min-w-[119px] h-[36px] flex items-center justify-center overflow-hidden rounded-lg group cursor-pointer"
          onClick={() => navigate(`${baseUrl}/typography`)}
        >
          <div 
              className="app-selector-indicator" 
              style={{ '--background-width': '0%' } as React.CSSProperties} 
          />
          <div className="flex items-center gap-2 relative z-10">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#667185] group-hover:text-[#30A84B] transition-colors">
              <polyline points="4 7 4 4 20 4 20 7" />
              <line x1="9" x2="15" y1="20" y2="20" />
              <line x1="12" x2="12" y1="4" y2="20" />
            </svg>
            <span className="font-sans font-medium text-[14px] text-[#667185] group-hover:text-[#30A84B] transition-colors">Typography</span>
          </div>
        </div>
      </div>
      {hasColors ? (
        <SelectedColorsView
          palettes={selectedPalettes}
          onAddClick={() => setIsColorModalOpen(true)}
          onDeletePalette={handleDeletePalette}
          onCustomPaletteClick={handleCustomPaletteClick}
          onUpdatePalette={handleUpdatePalette}
        />
      ) : (
        <ColorsEmptyState onAddColors={() => setIsColorModalOpen(true)} />
      )}
    </div>
  );
}
